
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CreateEcommercePriceInput } from '@/types/database/ecommerce-prices';

const formSchema = z.object({
  date: z.string().min(1, 'Date is required'),
  product_name: z.string().min(1, 'Product name is required'),
  price: z.string().min(1, 'Price is required'),
  location: z.string().min(1, 'Location is required'),
  product_remark: z.string().optional(),
});

interface EcommercePriceFormProps {
  onSuccess: () => void;
}

const EcommercePriceForm: React.FC<EcommercePriceFormProps> = ({ onSuccess }) => {
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      product_name: '',
      price: '',
      location: '',
      product_remark: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const priceData: CreateEcommercePriceInput = {
        date: values.date,
        product_name: values.product_name,
        price: parseFloat(values.price),
        location: values.location,
        product_remark: values.product_remark || undefined,
      };

      const { error } = await supabase
        .from('ecommerce_prices')
        .insert([priceData]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Ecommerce price added successfully',
      });

      form.reset();
      onSuccess();
    } catch (error) {
      console.error('Error adding ecommerce price:', error);
      toast({
        title: 'Error',
        description: 'Failed to add ecommerce price',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Add New Ecommerce Price</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="product_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter product name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="product_remark"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Product Remark (Optional)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Enter any additional remarks about the product"
                    className="min-h-[100px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full">
            Add Ecommerce Price
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default EcommercePriceForm;
